import React, { useState } from "react";
import { Divider, message } from "antd";
import {
  useSubscriptionMutation,
  useCancelSubscriptionMutation,
} from "api/pricingApi";
import { useVerifyUserQuery } from "api/authApi";
import cache from "utils/cache";
import { trackMeta, getMetaAttributionPayload } from "utils/metaPixel";
import {
  CardsWrapper,
  ProCardWrapper,
  InnerContainer,
  MainContainer,
  StyledHeading,
  StyledSubHeading,
  InnerWrapper,
  StyledPrice,
  StyledCategory,
  StyledButton,
  StyledCancelButton,
  StyledButtonEnterprise,
  StyledSubHeadingEnterprise,
  ButtonWrapper,
  StyledFeaturedHeading,
  StyledUl,
  StyledLi,
  PriceMetaRow,
  PriceLine,
  OriginalYearlyPrice,
  DiscountBadge,
} from "./styles";
// import { PayPalButtons } from "@paypal/react-paypal-js";

const PriceCards = ({ billingCycle = "MONTHLY" }) => {
  // const [isPayPalVisible, setPayPalVisible] = useState(false); // Manage PayPal button visibility
  // const handleSubscribeClick = () => {
  //   setPayPalVisible(true); // Show PayPal buttons on click
  // };
  const [Subscribe] = useSubscriptionMutation();
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
  const [loadingPlanKey, setLoadingPlanKey] = useState(null);
  const { data: verifiedUser, refetch: refetchUser } = useVerifyUserQuery();
  const user = verifiedUser || cache.getItem("user");
  const subscription = user?.subscription;
  const hasPreviousSubscription = Boolean(subscription);
  const isSponsored = Boolean(user?.isSponsored);
  const showTrialCopy = !hasPreviousSubscription && !isSponsored;

  const MONTHLY_PRICES = {
    BASIC: 29.99,
    PRO: 49.99,
  };
  const yearlyPrice = (monthlyPrice) => Number((monthlyPrice * 12 * 0.85).toFixed(2)).toFixed(2);
  const yearlyOriginalPrice = (monthlyPrice) => Number((monthlyPrice * 12).toFixed(2)).toFixed(2);
  const getPlanPrice = (type) =>
    billingCycle === "YEARLY" ? yearlyPrice(MONTHLY_PRICES[type]) : MONTHLY_PRICES[type];

  const isCurrentPlanSubscribed = (type) =>
    Boolean(
      subscription &&
      ["ACTIVE", "APPROVAL_PENDING"].includes(subscription.status) &&
      subscription.planType === type &&
      subscription.plan === billingCycle
    );

  const handleCancelSubscription = async () => {
    const response = await cancelSubscription();
    if (response?.error) {
      message.error(response?.error?.data?.message || "Failed to cancel subscription");
      return;
    }
    message.success("Subscription canceled successfully");
    await refetchUser();
  };

  const handleSubscription = async (type, gateway = "STRIPE") => {
    if (isSponsored || isCurrentPlanSubscribed(type)) {
      return;
    }
    const currentPlanKey = `${type}-${billingCycle}-${gateway}`;
    setLoadingPlanKey(currentPlanKey);
    const plan = billingCycle === "YEARLY" ? "YEARLY" : "MONTHLY";
    const attribution = getMetaAttributionPayload();
    trackMeta(
      "InitiateCheckout",
      {
        content_name: `${type}_${plan}`,
        currency: "USD",
        value: Number(getPlanPrice(type)),
      },
    );
    const response = await Subscribe({ type, plan, gateway, ...attribution });
    console.log("response", response);
    if (response?.error) {
      setLoadingPlanKey(null);
      message.error(response?.error?.data?.message);
    } else if (response?.data) {
      // Check if the response contains a link
      const link = response.data.link;
      // Redirect the user to the subscription link
      window.location.href = link; // Opens the link in the current tab
      // Or use window.open(link, '_blank'); // Opens the link in a new tab
    }
  };
  return (
    <MainContainer>
      <InnerContainer>
        <CardsWrapper>
          <StyledHeading>{`Starter`}</StyledHeading>
          <StyledSubHeading>{`Everything you need to get started with finding new winning products`}</StyledSubHeading>
          <Divider />
          <InnerWrapper>
            {billingCycle === "YEARLY" && (
              <PriceMetaRow>
                <OriginalYearlyPrice>{`$${yearlyOriginalPrice(MONTHLY_PRICES.BASIC)}`}</OriginalYearlyPrice>
                <DiscountBadge>{`15% OFF`}</DiscountBadge>
              </PriceMetaRow>
            )}
            <PriceLine>
              <StyledPrice>{`$${getPlanPrice("BASIC")}/`}</StyledPrice>
              <StyledCategory>{`${billingCycle === "YEARLY" ? "Yearly" : "Monthly"}`}</StyledCategory>
            </PriceLine>
            <ButtonWrapper>
              <StyledButton
                onClick={() =>
                  handleSubscription("BASIC", "STRIPE")
                }
                style={{
                  opacity: isCurrentPlanSubscribed("BASIC") || isSponsored ? 0.7 : 1,
                  pointerEvents: isCurrentPlanSubscribed("BASIC") || isSponsored ? "none" : "auto",
                }}
              >
                {isCurrentPlanSubscribed("BASIC")
                  ? "SUBSCRIBED"
                  : loadingPlanKey === `BASIC-${billingCycle}-STRIPE`
                    ? "LOADING..."
                    : isSponsored
                      ? "SPONSORED"
                      : hasPreviousSubscription
                      ? "SUBSCRIBE"
                      : "TRY FOR FREE"}
              </StyledButton>
              {isCurrentPlanSubscribed("BASIC") && (
                <StyledCancelButton
                  type="button"
                  size="small"
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                >
                  {isCancelling ? "Canceling…" : "Cancel subscription"}
                </StyledCancelButton>
              )}
            </ButtonWrapper>
            {showTrialCopy && (
              <StyledSubHeading>{`14-Day Free Trial. Cancel Anytime.`}</StyledSubHeading>
            )}
          </InnerWrapper>
          <StyledFeaturedHeading>{`What’s included`}</StyledFeaturedHeading>
          <StyledUl>
            {showTrialCopy && <StyledLi>{`14-days free trial`}</StyledLi>}
            <StyledLi>{`Automated Order Updates`}</StyledLi>
            <StyledLi>{`Automated Order Fulfillment (within 3-business days)`}</StyledLi>
            <StyledLi>{`Auto-Checkout`}</StyledLi>
            <StyledLi>{`24/7 Chat Support`}</StyledLi>
          </StyledUl>

          {/* {isPayPalVisible && (
            <PayPalButtons
              createSubscription={(data, actions) => {
                return actions.subscription.create({
                  plan_id: process.env.REACT_APP_PAYPAL_MONTHLY_BASIC_PLAN, // Use REACT_APP prefix for env variables
                  // Add vaulting option here if supported
                  vault: true,
                });
              }}
              onApprove={(data, actions) => {
                console.log("Subscription successful", data);
                // Handle subscription success, e.g., redirect to confirmation
              }}
              onError={(err) => {
                console.error("Subscription error:", err);
              }}
              style={{
                layout: "vertical",
                color: "gold",
                shape: "rect",
                label: "subscribe",
              }}
            />
          )} */}
        </CardsWrapper>

        {/* Pro and Enterprise Cards - unchanged */}
        <ProCardWrapper>
          <StyledHeading>{`Pro`}</StyledHeading>
          <StyledSubHeading>{`The most popular plan if you are serious about getting started`}</StyledSubHeading>
          <Divider />
          <InnerWrapper>
            {billingCycle === "YEARLY" && (
              <PriceMetaRow>
                <OriginalYearlyPrice>{`$${yearlyOriginalPrice(MONTHLY_PRICES.PRO)}`}</OriginalYearlyPrice>
                <DiscountBadge>{`15% OFF`}</DiscountBadge>
              </PriceMetaRow>
            )}
            <PriceLine>
              <StyledPrice>{`$${getPlanPrice("PRO")}/`}</StyledPrice>
              <StyledCategory>{`${billingCycle === "YEARLY" ? "Yearly" : "Monthly"}`}</StyledCategory>
            </PriceLine>
            <ButtonWrapper>
              <StyledButton
                onClick={() => handleSubscription("PRO", "STRIPE")}
                style={{
                  opacity: isCurrentPlanSubscribed("PRO") || isSponsored ? 0.7 : 1,
                  pointerEvents: isCurrentPlanSubscribed("PRO") || isSponsored ? "none" : "auto",
                }}
              >
                {isCurrentPlanSubscribed("PRO")
                  ? "SUBSCRIBED"
                  : loadingPlanKey === `PRO-${billingCycle}-STRIPE`
                    ? "LOADING..."
                    : isSponsored
                      ? "SPONSORED"
                      : hasPreviousSubscription
                      ? "SUBSCRIBE"
                      : "TRY FOR FREE"}
              </StyledButton>
              {isCurrentPlanSubscribed("PRO") && (
                <StyledCancelButton
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                >
                  {isCancelling ? "Canceling…" : "Cancel subscription"}
                </StyledCancelButton>
              )}
            </ButtonWrapper>
            {showTrialCopy && (
              <StyledSubHeading>{`14-Day Free Trial. Cancel Anytime.`}</StyledSubHeading>
            )}
          </InnerWrapper>
          <StyledFeaturedHeading>{`What’s included`}</StyledFeaturedHeading>
          <StyledUl>
            {showTrialCopy && <StyledLi>{`14-days free trial`}</StyledLi>}
            <StyledLi>{`Everything in Starter, PLUS:`}</StyledLi>
            <StyledLi>{`Private Source Request`}</StyledLi>
            <StyledLi>{`TikTok Shop Weekly Trending Report`}</StyledLi>
            <StyledLi>{`Appeal Management Service`}</StyledLi>
            <StyledLi>{`24/7 1:1 Account Manager Support`}</StyledLi>
          </StyledUl>
        </ProCardWrapper>

        <CardsWrapper>
          <StyledHeading>{`Enterprise`}</StyledHeading>
          <StyledSubHeading>{`The biggest plan to maximize your potential of success`}</StyledSubHeading>
          <Divider />
          <InnerWrapper>
            <StyledPrice>{`Custom`}</StyledPrice>
            <ButtonWrapper>
              <StyledButtonEnterprise onClick={() => window.location.href = "mailto:sales@ecomdropship.com"}>{`Contact Sales`}</StyledButtonEnterprise>
            </ButtonWrapper>
            <StyledSubHeadingEnterprise>{`14-Day Free Trial. Cancel Anytime.`}</StyledSubHeadingEnterprise>
          </InnerWrapper>
          <StyledFeaturedHeading>{`What’s included`}</StyledFeaturedHeading>
          <StyledUl>
            <StyledLi>{`Everything in Pro, PLUS:`}</StyledLi>
            <StyledLi>{`Multiple Shop management`}</StyledLi>
            <StyledLi>{`Master Account management`}</StyledLi>
            <StyledLi>{`1:1 TTS Expert Support`}</StyledLi>
            <StyledLi>{`Branded Products`}</StyledLi>
          </StyledUl>
        </CardsWrapper>
      </InnerContainer>
    </MainContainer>
  );
};

export default PriceCards;
